// Inline edit mode for a transaction row.
// Single <tr> that spans all columns of the register as one cell, with a
// compact flex-row layout inside: Date, Ref No, Type, Payee, Account, Memo,
// Payment, Deposit grouped left-to-right at fixed widths, then Save /
// Cancel / Edit / Delete buttons immediately after. Wraps gracefully on
// narrower viewports. Esc cancels; Enter saves.

import React, { useEffect, useState } from "react";
import type { BankTransaction } from "../types";
import type { ColumnLayout, BankHistorySettings } from "../constants";
import { TRANSACTION_TYPE_OPTIONS } from "../constants";
import DatePicker from "../../../../../components/DatePicker";

interface EditTransactionRowProps {
  original: BankTransaction & { runningBalance: number };
  layout: ColumnLayout;
  settings: BankHistorySettings;
  onSave: (updated: BankTransaction) => void;
  onCancel: () => void;
  onDelete: () => void;
  onEditFull?: () => void;
}

function parseAmount(v: string): number | undefined {
  const cleaned = v.replace(/[$,\s]/g, "");
  if (cleaned === "") return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

const LABEL_CLASS = "text-[9.5px] font-bold text-ink3 uppercase tracking-wider mb-1";
const INPUT_CLASS = "bg-white border border-line rounded-md px-2 py-[5px] text-[12.5px] text-ink outline-none focus:border-teal-dark";
const INPUT_RIGHT_CLASS = INPUT_CLASS + " text-right tabular-nums";
const SELECT_CLASS = "bg-white border border-line rounded-md px-2 py-[5px] text-[12px] text-ink2 outline-none focus:border-teal-dark cursor-pointer";
const BTN_SECONDARY = "text-[11.5px] text-ink bg-white border border-line rounded-md px-3 py-[6px] font-semibold cursor-pointer hover:bg-panel whitespace-nowrap";
const BTN_DANGER = "text-[11.5px] text-red-600 bg-white border border-[#F3C9C9] rounded-md px-3 py-[6px] font-semibold cursor-pointer hover:bg-[#FCE8E8] whitespace-nowrap";
const BTN_PRIMARY = "text-[11.5px] text-white bg-teal-dark border-none rounded-md px-4 py-[6px] font-semibold cursor-pointer hover:brightness-110 whitespace-nowrap";

export default function EditTransactionRow({
  original,
  layout,
  settings,
  onSave,
  onCancel,
  onDelete,
  onEditFull,
}: EditTransactionRowProps) {
  const [dateIso, setDateIso] = useState<string>(original.date);
  const [refNo, setRefNo] = useState<string>(original.refNo ?? "");
  const [type, setType] = useState<string>(original.type);
  const [payee, setPayee] = useState<string>(original.payee ?? "");
  const [linkedAccountName, setLinkedAccountName] = useState<string>(original.linkedAccountName ?? "");
  const [memo, setMemo] = useState<string>(original.memo ?? "");
  const [leftVal, setLeftVal] = useState<string>(
    original[layout.leftFieldKey] != null ? String(original[layout.leftFieldKey]) : ""
  );
  const [rightVal, setRightVal] = useState<string>(
    original[layout.rightFieldKey] != null ? String(original[layout.rightFieldKey]) : ""
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const handleSave = () => {
    const next: BankTransaction = {
      ...original,
      date: dateIso,
      refNo,
      type: type as BankTransaction["type"],
      payee,
      linkedAccountName,
      memo,
    };
    const parsedLeft = parseAmount(leftVal);
    const parsedRight = parseAmount(rightVal);
    (next as unknown as Record<string, number | undefined>)[layout.leftFieldKey] = parsedLeft;
    (next as unknown as Record<string, number | undefined>)[layout.rightFieldKey] = parsedRight;
    onSave(next);
  };

  const handleDelete = () => {
    if (window.confirm(`Delete this transaction? This cannot be undone.`)) {
      onDelete();
    }
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  };

  const totalColumns =
    5 +
    (settings.columns.memo ? 1 : 0) +
    (settings.columns.attachments ? 1 : 0) +
    (settings.columns.reconcileStatus ? 1 : 0) +
    (settings.columns.runningBalance ? 1 : 0);

  return (
    <tr className="bg-[#EEF4FB]">
      <td colSpan={totalColumns} className="px-4 py-3 border-b border-lineSoft">
        <div className="flex flex-wrap items-end gap-x-2.5 gap-y-2">
          <div className="flex flex-col">
            <label className={LABEL_CLASS}>Date</label>
            <div style={{ width: 130 }}>
              <DatePicker value={dateIso} onChange={setDateIso} ariaLabel="Transaction date" />
            </div>
          </div>
          <div className="flex flex-col">
            <label className={LABEL_CLASS}>Ref No</label>
            <input
              type="text"
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Ref No."
              className={INPUT_CLASS}
              style={{ width: 100 }}
              autoFocus
            />
          </div>
          <div className="flex flex-col">
            <label className={LABEL_CLASS}>Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className={SELECT_CLASS}
              style={{ width: 130 }}
            >
              {TRANSACTION_TYPE_OPTIONS.filter((o) => o.value !== "all").map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className={LABEL_CLASS}>Payee</label>
            <input
              type="text"
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Payee"
              className={INPUT_CLASS}
              style={{ width: 180 }}
            />
          </div>
          <div className="flex flex-col">
            <label className={LABEL_CLASS}>Account</label>
            <input
              type="text"
              value={linkedAccountName}
              onChange={(e) => setLinkedAccountName(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="Account"
              className={INPUT_CLASS}
              style={{ width: 180 }}
            />
          </div>
          {settings.columns.memo && (
            <div className="flex flex-col">
              <label className={LABEL_CLASS}>Memo</label>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                onKeyDown={onInputKey}
                placeholder="Memo"
                className={INPUT_CLASS}
                style={{ width: 220 }}
              />
            </div>
          )}
          <div className="flex flex-col">
            <label className={LABEL_CLASS}>{layout.leftLabel}</label>
            <input
              type="text"
              value={leftVal}
              onChange={(e) => setLeftVal(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="0.00"
              className={INPUT_RIGHT_CLASS}
              style={{ width: 100 }}
            />
          </div>
          <div className="flex flex-col">
            <label className={LABEL_CLASS}>{layout.rightLabel}</label>
            <input
              type="text"
              value={rightVal}
              onChange={(e) => setRightVal(e.target.value)}
              onKeyDown={onInputKey}
              placeholder="0.00"
              className={INPUT_RIGHT_CLASS}
              style={{ width: 100 }}
            />
          </div>
          <div className="flex items-end gap-1.5">
            <button type="button" onClick={handleDelete} className={BTN_DANGER}>Delete</button>
            <button type="button" onClick={() => onEditFull?.()} className={BTN_SECONDARY}>Edit</button>
            <button type="button" onClick={onCancel} className={BTN_SECONDARY}>Cancel</button>
            <button type="button" onClick={handleSave} className={BTN_PRIMARY}>Save</button>
          </div>
        </div>
      </td>
    </tr>
  );
}